import { DatePickerDayInterface } from './date-picker-day.interface';
import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { range } from '../../utilities';
import { format, add, sub, set, startOfDay, getYear, getMonth, getDate, getDay, getDaysInMonth, isBefore, isAfter, isSameDay, getDayOfYear } from 'date-fns';
import AvailabilityViewModel from '@app/models/availability-view-model';
import { UserService, DateService, LogService, SpinnerService, ValueService } from '@app/services';
import { NgClass } from '@angular/common';

@Component({
    selector: "date-picker",
    templateUrl: "./date-picker.component.html",
    styleUrls: ["./date-picker.component.less"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgClass]
})
export class DatePickerComponent implements OnInit {
  private readonly _componentName = 'DatePickerComponent';

  private _availabilities: AvailabilityViewModel[];

  private _loading: boolean;

  public message: string;

  @Input() text = 'mm/dd/yyyy';
  @Input() pullRight = false;
  spinnerUrl: string;
  @Input()
  set availabilities(value: AvailabilityViewModel[]) {
    this._availabilities = value;
    this.buildMonth();
  }
  get availabilities(): AvailabilityViewModel[] {
    return this._availabilities;
  }
  @Input() showSelector = false;
  @Input()
  set month(value: Date) {
    if (value !== undefined) {
      this._month = value;
      this.buildMonth();
    }
  }
  get month(): Date {
    return this._month;
  }

  get formattedMonth(): string {
    return format(this._month, 'MMMM');
  }

  get formattedYear(): string {
    return format(this._month, 'yyyy');
  }

  /**
   * Gets the year
   */
  get year(): number {
    return getYear(this._month);
  }

  @Output() monthChanged = new EventEmitter<Date>();
  @Output() dateChanged = new EventEmitter<Date>();
  @Output() clossed = new EventEmitter<undefined>();

  selectionDayTxt: string;
  weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  weeks = [];

  startDay: DatePickerDayInterface;
  endDay: DatePickerDayInterface;

  dayNumbers: Date[];
  _month = new Date();

  constructor(
    readonly elem: ElementRef,
    private userService: UserService,
    private dateHelper: DateService,
    private changeDetector: ChangeDetectorRef,
    private log: LogService,
    private valueHelper: ValueService,
    private spinnerService: SpinnerService
  ) {
    const root = document.getElementsByTagName('html')[0];
    root.addEventListener(
      'click',
      (event) => {
        if (
          this.showSelector &&
          event.target &&
          this.elem.nativeElement !== event.target &&
          !this.elem.nativeElement.contains(event.target)
        ) {
          this.showSelector = false;
          this.clossed.emit();
        }
      },
      false
    );
  }

  ngOnInit() {
    this.spinnerUrl = this.spinnerService.getMagnifySpinnerUrl();

    this.month = new Date();
    this.buildMonth();
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  /**
   * Handles the button click event
   */
  public onButtonClick() {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.onButtonClick()`);
    }

    this.showSelector = true;
  }

  public get loading(): boolean {
    return this._loading;
  }
  public set loading(v: boolean) {
    this._loading = v;
    this.changeDetector.detectChanges();
  }

  public createCellId(day: DatePickerDayInterface){
    if (day.isCurrentMonth){
      return `cell_${day.dayOfMonth}`;
    }

    return undefined;
  }

  public getCssClass(day: DatePickerDayInterface): string {
    // console.log(`${this._componentName}.getCssClass day=`,day);

    if (!day.isCurrentMonth) {
      return 'not-current-month';
    }

    if (day.status !== 2 && isBefore(startOfDay(day.date), startOfDay(new Date()))) {
      return 'past';
    }

    if (day.selected === true) {
      return 'selected';
    }

    return day.status === 1 ? 'open date-picker-day-open' : 'closed date-picker-day-closed';
  }

  public selectDay(day: DatePickerDayInterface) {
    // do not allow users to select unavailable days
    if (day.status !== 1) {
      return;
    }

    this.weeks.forEach((w) => {
      w.days.forEach((d) => (d.selected = false));
    });

    day.selected = true;
    this.selectionDayTxt = format(day.date, 'MM/dd/yyyy');
    this.showSelector = false;

    this.loading = false;
    this.dateChanged.emit(day.date);
  }

  public prevMonth() {
    this.month = set(this.month, { month: getMonth(this.month) - 1, date: 1 });
    this.month = startOfDay(this.month);

    this.loading = true;
    this.monthChanged.emit(this.month);
  }

  public nextMonth() {
    this.month = set(this.month, { month: getMonth(this.month) + 1, date: 1 });
    this.month = startOfDay(this.month);

    this.loading = true;
    this.monthChanged.emit(this.month);
  }

  public prevYear() {
    this.month = set(this.month, { year: getYear(this.month) - 1, date: 1 });
    this.month = startOfDay(this.month);

    this.loading = true;

    this.monthChanged.emit(this.month);
  }

  public nextYear() {
    this.month = set(this.month, { year: getYear(this.month) + 1, date: 1 });
    this.month = startOfDay(this.month);

    this.loading = true;

    this.monthChanged.emit(this.month);
  }

  public get isAdmin(): boolean {
    return this.userService.isAdmin;
  }

  truncateTime(date: Date): Date {
    return startOfDay(date);
  }

  buildMonth() {
    if (this.availabilities === undefined) {
      return;
    }

    let done = false;
    let date = new Date(this.month);
    date = this.truncateTime(set(date, { date: 1 }));
    let monthIndex = getMonth(date);
    let count = 0;

    // figure out what date the first Sunday block should be.
    const dayOfWeek = getDay(date);
    if (dayOfWeek > 0) {
      date = sub(date, { days: dayOfWeek });
    }

    this.weeks = [];
    while (!done) {
      this.weeks.push({
        days: this.buildWeek(new Date(date), this.availabilities),
      });
      date = add(date, { weeks: 1 });
      done = count++ > 2 && monthIndex !== getMonth(date);
      monthIndex = getMonth(date);
    }

    this.loading = false;
  }

  buildWeek(date: Date, availabilityModels: AvailabilityViewModel[]): DatePickerDayInterface[] {
    const key = format(date, 'yyyy-MM-dd');

    if (!date) {

    }

    if (this.verboseLogging) {
      console.log(`${this._componentName}.buildWeek key=`, key);
    }

    if (key == '2020-04-19') {
      //
    }

    date = sub(new Date(date), { days: 1 });
    const days = range(1, 7)
      .map((n) => add(new Date(date), { days: n }))
      .map((date) => {
        if (this.verboseLogging) {
          console.log(`${this._componentName}.buildWeek map date=${date}`);
        }

        const availabilityViewModel = this.getAvailability(date);
        // console.log(`${this._componentName}.buildWeek map  [${date}] availabilityViewModel=`,availabilityViewModel);

        let availabilityModel = undefined;

        if (availabilityViewModel && availabilityViewModel.availability) {
          availabilityModel = availabilityViewModel.availability || undefined;
          // console.log(`${this._componentName}.buildWeek  availabilityModel=`, availabilityModel);
        }

        const calendarDay = {
          dayOfMonth: getDate(date),
          isCurrentMonth: getMonth(date) === getMonth(this.month),
          isToday: getDayOfYear(date) === getDayOfYear(new Date()),
          date: date,
          status: 0,
          price: undefined,
          availability: availabilityViewModel,
        } as DatePickerDayInterface;

        if (availabilityViewModel !== undefined) {
          const availability = availabilityViewModel.availability;

          if (availabilityViewModel.statusId) {
            calendarDay.status = availabilityViewModel.statusId;
          } else {
            calendarDay.status = 0;
          }

          if (availability && availability.price) {
            calendarDay.price = availability.price;
          }

          calendarDay.title = '';
        }
        return calendarDay;
      });

    if (this.verboseLogging) {
      console.log(`${this._componentName}.buildWeek() Days:`, days);
    }

    return days;
  }

  /**
   * name
   */
  public onMouseOver(day: DatePickerDayInterface) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.onMouseOver day=`, day);
    }

    let newMessage = null;

    if (this.dateHelper.isPast(day.date)) {
      newMessage = 'This date is in the past.';
    } else {
      const a = this.getAvailability(day.date);

      if (this.verboseLogging) {
        console.log(`${this._componentName}.onMouseOver day=${day.date} closedReason=${this.valueHelper.getSafeValue(a, 'closedReason')}`);
      }

      if (a) {
        if (a.statusReason) {
          newMessage = a.statusReason;
        } else {
          if (a.statusId === 1) {
            newMessage = 'This date is available!';
          }
        }
      }
    }

    this.message = newMessage;
    this.changeDetector.detectChanges();
  }

  public onMouseOut(day: DatePickerDayInterface) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.onMouseOut day=`, day);
    }

    this.message = null;
    this.changeDetector.detectChanges();
  }

  /**
   * Gets the AvailabilityViewModel for the date from availabilities
   */

  public getAvailability(date: Date): AvailabilityViewModel {
    const m = this.availabilities.find((a) => this.dateHelper.isDateEqual(a.date, date));

    if (this.verboseLogging) {
      console.log(`${this._componentName}.getAvailability(${date}) found avail: `, m);
    }

    if (!m) {
      if (this.verboseLogging) {
        console.log(`${this._componentName}.getAvailability(${date}) availabilities: `, this.availabilities);
      }
    }

    return m;
  }
}
